import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, CornerDownLeft } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Slim slide-down overlay triggered by the mobile search icon. Fetches
 * /api/search?q= as the user types (debounced), shows top 3 per group, and
 * lets them jump straight to a result or hit Enter to open the full /search page.
 * Rendered as a full-viewport `fixed` layer so it works at any header layout.
 */
export default function MobileSearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus the input when opened + reset state when closed
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      setQ(""); setGroups([]);
    }
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setGroups([]); return; }
    setLoading(true);
    const timer = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(term)}`)
        .then(r => {
          const enabled = (r.data.groups || []).filter(g => g.enabled && g.results?.length);
          setGroups(enabled);
        })
        .catch(() => setGroups([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [q, open]);

  const submit = (e) => {
    e?.preventDefault?.();
    const term = q.trim();
    if (!term) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  if (!open) return null;

  const total = groups.reduce((acc, g) => acc + g.results.length, 0);

  return (
    <div data-testid="mobile-search-overlay"
         className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm"
         onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-lg animate-in slide-in-from-top-2 fade-in-0 duration-200"
           onClick={e => e.stopPropagation()}>
        <form onSubmit={submit}
              className="max-w-3xl mx-auto flex items-center gap-2 px-4 py-3">
          <Search className="w-5 h-5 text-slate-400 shrink-0"/>
          <input ref={inputRef}
                 data-testid="mobile-search-input"
                 value={q}
                 onChange={e => setQ(e.target.value)}
                 placeholder="Search Site"
                 className="flex-1 bg-transparent outline-none text-base placeholder:text-slate-400 text-slate-900 dark:text-slate-100"/>
          {q.trim() && (
            <button type="button" onClick={() => setQ("")} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Clear">
              <X className="w-4 h-4"/>
            </button>
          )}
          <button type="button" onClick={onClose}
                  data-testid="mobile-search-close"
                  className="ml-1 p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                  aria-label="Close">
            <X className="w-5 h-5"/>
          </button>
        </form>

        {/* Results */}
        <div className="max-w-3xl mx-auto px-4 pb-4 max-h-[70vh] overflow-y-auto">
          {q.trim() === "" && (
            <p className="text-sm text-slate-400 py-6 text-center italic">
              Start typing to search across pathways, blogs, and glossary terms.
            </p>
          )}
          {q.trim() !== "" && !loading && total === 0 && (
            <p data-testid="mobile-search-empty" className="text-sm text-slate-400 py-6 text-center italic">
              No matches for &ldquo;{q}&rdquo;.
            </p>
          )}
          {loading && q.trim() && (
            <p className="text-sm text-slate-400 py-6 text-center italic">Searching…</p>
          )}
          {groups.map(g => (
            <div key={g.key} className="mb-4" data-testid={`mobile-search-group-${g.key}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 px-1">
                {g.label || g.key}
              </div>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                {g.results.slice(0, 3).map(r => (
                  <li key={r.id}>
                    <Link to={r.href}
                          data-testid={`mobile-search-hit-${g.key}-${r.id}`}
                          onClick={onClose}
                          className="flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 mt-1 shrink-0"/>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 line-clamp-1">{r.title}</div>
                        {r.subtitle && (
                          r.subtitle_html
                            ? <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 [&_mark]:bg-yellow-100 [&_mark]:text-slate-900 [&_mark]:rounded [&_mark]:px-0.5"
                                   dangerouslySetInnerHTML={{__html: r.subtitle}}/>
                            : <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{r.subtitle}</div>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {total > 0 && (
            <button data-testid="mobile-search-all"
                    onClick={submit}
                    className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-sky-700 hover:text-sky-900 dark:text-sky-400 py-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              See all results for &ldquo;{q}&rdquo;
              <CornerDownLeft className="w-4 h-4"/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
